import * as Dialog from "@radix-ui/react-dialog";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, CheckCircle2, CircleDashed, KeyRound, Pencil, Plus, RefreshCw, Trash2, Truck, X, XCircle } from "lucide-react";
import { useState, type ReactNode } from "react";
import { toast } from "sonner";
import { PageHeader, useTenant } from "@/components/AppShell";
import { Badge, Button, Card, cx, EmptyState, Input, Skeleton } from "@/components/ui";
import { api, ApiError, canManage, type CarrierAccount, type CarrierAccountInput, type CarrierCheck, type CarrierInfo } from "@/lib/api";
import { fmtNumber, fmtRelative } from "@/lib/format";

import { t, msg } from "@/lib/i18n";
import { serverText } from "@/lib/server-text";
const errMsg = (e: unknown, fallback: string) => (e instanceof ApiError ? ((e.body as { message?: string })?.message ?? fallback) : fallback);

const STATUS: Record<CarrierAccount["status"], { label: string; tone: "success" | "danger" | "neutral"; icon: ReactNode }> = {
  ok: { label: msg("Zugang geprüft"), tone: "success", icon: <CheckCircle2 className="size-3" /> },
  failed: { label: msg("Anmeldung fehlgeschlagen"), tone: "danger", icon: <XCircle className="size-3" /> },
  unchecked: { label: msg("Noch nicht geprüft"), tone: "neutral", icon: <CircleDashed className="size-3" /> },
};

type Method = { id: string; carrier: string | null };

/** Ergebnis der Anmeldeprüfung als Meldung */
function announce(check: CarrierCheck | null, saved: string) {
  if (!check) return toast.success(saved);
  if (check.ok) toast.success(`${saved} – ${serverText(check.message)}`);
  else toast.error(check.message, { description: msg("Gespeichert, aber ohne gültigen Zugang gibt es keine Sendungsverfolgung.") });
}

/** Einstellungen → Versandkonten (Kunde) */
export function CarrierAccountsPage() {
  const { tenantId, role } = useTenant();
  const manage = canManage(role);
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["carrier-accounts", tenantId], queryFn: () => api.carrierAccounts.list(tenantId) });
  const [edit, setEdit] = useState<CarrierAccount | "new" | null>(null);
  const refresh = () => void qc.invalidateQueries({ queryKey: ["carrier-accounts", tenantId] });

  const check = useMutation({
    mutationFn: (id: string) => api.carrierAccounts.check(tenantId, id),
    onSuccess: (r) => { (r.ok ? toast.success : toast.error)(r.message); refresh(); },
    onError: (e) => toast.error(errMsg(e, "Prüfung fehlgeschlagen")),
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.carrierAccounts.remove(tenantId, id),
    onSuccess: () => { toast.success("Versandkonto gelöscht"); refresh(); },
    onError: (e) => toast.error(errMsg(e, "Löschen fehlgeschlagen")),
  });

  const d = q.data;
  return (
    <div className="pb-16">
      <PageHeader
        title={t("Versandkonten")}
        description={t("Eure Zugänge bei Versanddienstleistern. Welches Konto gilt, hängt an der Versandart: Versand über Logiful läuft über das Konto von Logiful, eure eigene Versandart über euer Konto.")}
        actions={manage && d ? <Button variant="primary" icon={<Plus className="size-4" />} onClick={() => setEdit("new")}>{t("Konto hinzufügen")}</Button> : undefined}
      />
      <div className="px-4 md:px-8 space-y-6 max-w-4xl">
        {d && !d.apiReady && (
          <p className="rounded-xl border border-warning/30 bg-warning-soft/50 px-4 py-3 text-[13px]">{t("Die DHL-Schnittstelle wird bei Logiful gerade eingerichtet. Zugänge könnt ihr schon hinterlegen – geprüft werden sie, sobald sie aktiv ist.")}</p>
        )}

        <section>
          <h2 className="text-[13px] font-semibold mb-2">{t("Versandarten und Konto")}</h2>
          <Card className="divide-y divide-border">
            {!d ? (
              <div className="p-4 space-y-2"><Skeleton className="h-5 w-2/3" /><Skeleton className="h-5 w-1/2" /></div>
            ) : d.methods.length === 0 ? (
              <p className="px-4 py-5 text-[13px] text-fg-muted">{t("Noch keine Versandarten – sie erscheinen, sobald euer Lager verbunden ist.")}</p>
            ) : (
              d.methods.map((m) => (
                <div key={m.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2.5 text-[13px]">
                  <Truck className="size-4 text-fg-subtle" />
                  <span className="font-medium">{m.id}</span>
                  {m.carrier && <span className="text-fg-muted">{m.carrier}</span>}
                  <span className="ml-auto">
                    {m.account ? (
                      <Badge tone={m.account.owner === "logiful" ? "info" : "neutral"}>{m.account.owner === "logiful" ? t("Konto von Logiful") : m.account.label}</Badge>
                    ) : (
                      <span className="text-[12px] text-fg-subtle">{t("kein Konto – keine Sendungsverfolgung")}</span>
                    )}
                  </span>
                </div>
              ))
            )}
          </Card>
        </section>

        <section>
          <h2 className="text-[13px] font-semibold mb-2">{t("Eigene Konten")}</h2>
          {!d ? (
            <Skeleton className="h-24" />
          ) : d.accounts.length === 0 ? (
            <Card>
              <EmptyState
                icon={<KeyRound className="size-5" />}
                title={t("Keine eigenen Versandkonten")}
                action={manage ? <Button icon={<Plus className="size-4" />} onClick={() => setEdit("new")}>{t("Konto hinzufügen")}</Button> : undefined}
              >
                {t("Nur nötig, wenn Logiful eure Pakete über euren eigenen DHL-Vertrag verschickt. Versand über Logiful verfolgen wir mit dem Konto von Logiful.")}
              </EmptyState>
            </Card>
          ) : (
            <div className="space-y-3">
              {d.accounts.map((a) => (
                <AccountCard
                  key={a.id}
                  a={a}
                  manage={manage}
                  checking={check.isPending && check.variables === a.id}
                  onCheck={() => check.mutate(a.id)}
                  onEdit={() => setEdit(a)}
                  onDelete={() => { if (confirm(`Versandkonto „${a.label}“ löschen? Die zugeordneten Versandarten haben danach kein Konto mehr.`)) remove.mutate(a.id); }}
                />
              ))}
            </div>
          )}
        </section>
        <GkpHint />
      </div>
      {d && edit && (
        <AccountDialog
          account={edit === "new" ? null : edit}
          carriers={d.carriers}
          methods={d.methods}
          onClose={() => setEdit(null)}
          save={(body, id) => (id ? api.carrierAccounts.update(tenantId, id, body) : api.carrierAccounts.create(tenantId, body as CarrierAccountInput))}
          onSaved={refresh}
        />
      )}
    </div>
  );
}

/** Logiful intern: eigene Konten (für alle Kunden) und Überblick über die Kundenkonten */
export function AdminCarrierAccountsPage() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["admin-carrier-accounts"], queryFn: api.carrierAccounts.admin });
  const me = useTenant().me;
  const admin = me.user.staffRole === "staff_admin";
  const [edit, setEdit] = useState<CarrierAccount | "new" | null>(null);
  const refresh = () => void qc.invalidateQueries({ queryKey: ["admin-carrier-accounts"] });
  const check = useMutation({
    mutationFn: (id: string) => api.carrierAccounts.adminCheck(id),
    onSuccess: (r) => { (r.ok ? toast.success : toast.error)(r.message); refresh(); },
    onError: (e) => toast.error(errMsg(e, "Prüfung fehlgeschlagen")),
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.carrierAccounts.adminRemove(id),
    onSuccess: () => { toast.success("Versandkonto gelöscht"); refresh(); },
    onError: (e) => toast.error(errMsg(e, "Löschen fehlgeschlagen")),
  });
  const d = q.data;
  return (
    <div className="pb-16">
      <PageHeader
        title={t("Versandkonten")}
        description={t("Konten von Logiful gelten für alle Kunden, je Versandart. Kunden mit eigenem Vertrag pflegen ihre Konten selbst – hier der Überblick.")}
        actions={admin && d ? <Button variant="primary" icon={<Plus className="size-4" />} onClick={() => setEdit("new")}>{t("Logiful-Konto hinzufügen")}</Button> : undefined}
      />
      <div className="px-4 md:px-8 space-y-6 max-w-4xl">
        {d && !d.apiReady && (
          <p className="rounded-xl border border-warning/30 bg-warning-soft/50 px-4 py-3 text-[13px]">
            <span className="font-medium">{t("DHL_API_KEY fehlt.")}</span> {t("Den API-Schlüssel der Logiful-App aus dem DHL-Entwicklerportal in Render eintragen (DHL_API_KEY in der gemeinsamen Gruppe) – bis dahin keine Prüfung und keine Sendungsverfolgung.")}
          </p>
        )}
        {d && <ApiKeyCard hint={d.apiKeyHint ?? null} admin={admin} unified={d.unified ?? null} />}
        <section>
          <h2 className="text-[13px] font-semibold mb-2">{t("Konten von Logiful")}</h2>
          {!d ? (
            <Skeleton className="h-24" />
          ) : d.logiful.length === 0 ? (
            <Card><EmptyState icon={<KeyRound className="size-5" />} title={t("Noch kein Logiful-Konto")}>{t("Zugang des Logiful-Geschäftskundenportals hinterlegen und den Logiful-Versandarten zuordnen.")}</EmptyState></Card>
          ) : (
            <div className="space-y-3">
              {d.logiful.map((a) => (
                <AccountCard key={a.id} a={a} manage={admin} checking={check.isPending && check.variables === a.id} onCheck={() => check.mutate(a.id)} onEdit={() => setEdit(a)} onDelete={() => { if (confirm(`Logiful-Konto „${a.label}“ löschen? Alle Kunden verlieren für die zugeordneten Versandarten die Sendungsverfolgung.`)) remove.mutate(a.id); }} />
              ))}
            </div>
          )}
        </section>
        <section>
          <h2 className="text-[13px] font-semibold mb-2">{t("Konten der Kunden")}</h2>
          <Card className="divide-y divide-border">
            {!d ? (
              <div className="p-4"><Skeleton className="h-5 w-1/2" /></div>
            ) : d.customers.length === 0 ? (
              <p className="px-4 py-5 text-[13px] text-fg-muted">{t("Noch kein Kunde hat ein eigenes Versandkonto hinterlegt.")}</p>
            ) : (
              d.customers.map((a) => (
                <div key={a.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2.5 text-[13px]">
                  <span className="font-medium">{a.tenantName}</span>
                  <span className="text-fg-muted">{t(a.label)}</span>
                  <span className="text-fg-subtle text-[12px]">{a.methods.join(", ") || t("keine Versandart")}</span>
                  <span className="ml-auto flex items-center gap-2">
                    <Badge tone={STATUS[a.status].tone} icon={STATUS[a.status].icon}>{t(STATUS[a.status].label)}</Badge>
                    <Button size="sm" variant="ghost" icon={<RefreshCw className={cx("size-3.5", check.isPending && check.variables === a.id && "animate-spin")} />} onClick={() => check.mutate(a.id)}>{t("Prüfen")}</Button>
                  </span>
                </div>
              ))
            )}
          </Card>
        </section>
        <GkpHint />
      </div>
      {d && edit && (
        <AccountDialog
          account={edit === "new" ? null : edit}
          carriers={d.carriers}
          methods={d.methods}
          scopeNote={t("Gilt für alle Kunden mit diesen Versandarten (außer ein Kunde hat dafür ein eigenes Konto).")}
          onClose={() => setEdit(null)}
          save={(body, id) => (id ? api.carrierAccounts.adminUpdate(id, body) : api.carrierAccounts.adminCreate(body as CarrierAccountInput))}
          onSaved={refresh}
        />
      )}
    </div>
  );
}

/** DHL-Schnittstelle: hinterlegten Schlüssel erkennen und testen, ohne ihn anzuzeigen */
function ApiKeyCard({ hint, admin, unified }: { hint: string | null; admin: boolean; unified: { today: number; limit: number } | null }) {
  const [key, setKey] = useState("");
  const [result, setResult] = useState<{ ok: boolean; message: string; hint: string | null; source: string } | null>(null);
  const test = useMutation({
    mutationFn: (k?: string) => api.carrierAccounts.adminTestKey(k),
    onSuccess: (r) => setResult(r),
    onError: (e) => toast.error(errMsg(e, "Test fehlgeschlagen")),
  });
  return (
    <Card className="p-4">
      <div className="flex flex-wrap items-start gap-3">
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-[14px]">{t("DHL-Schnittstelle (API-Schlüssel der Logiful-App)")}</p>
          <p className="text-[12.5px] text-fg-muted">{t("Hinterlegt in Render als DHL_API_KEY:")} {hint ? <span className="font-mono text-fg">{t(hint)}</span> : <span className="text-danger">{t("nicht hinterlegt")}</span>} {t("– mit Anfang und Ende des Schlüssels im DHL-Portal vergleichen.")}</p>
        </div>
        {admin && hint && <Button size="sm" icon={<RefreshCw className={cx("size-3.5", test.isPending && !key && "animate-spin")} />} onClick={() => test.mutate(undefined)} disabled={test.isPending}>{t("Hinterlegten testen")}</Button>}
      </div>
      {admin && (
        <form className="mt-3 flex flex-wrap gap-2" onSubmit={(e) => { e.preventDefault(); if (key.trim()) test.mutate(key.trim()); }}>
          <Input type="password" autoComplete="off" value={key} onChange={(e) => setKey(e.target.value)} placeholder={t("Schlüssel aus dem DHL-Portal einfügen")} className="flex-1 min-w-[220px]" aria-label={t("API-Schlüssel zum Testen")} />
          <Button variant="secondary" loading={test.isPending && !!key} disabled={!key.trim()}>{t("Schlüssel testen")}</Button>
        </form>
      )}
      {result && (
        <p className={cx("mt-2.5 flex gap-1.5 text-[12.5px]", result.ok ? "text-success" : "text-danger")}>
          {result.ok ? <CheckCircle2 className="size-4 shrink-0" /> : <XCircle className="size-4 shrink-0" />}
          <span>
            {result.source === "input" ? t("Eingegebener") : t("Hinterlegter")} {t("Schlüssel")} {result.hint ? <span className="font-mono">{t(result.hint)}</span> : null}: {serverText(result.message)}
            {result.ok && result.source === "input" && hint && result.hint !== hint && <span className="block text-fg-muted">{t("Er unterscheidet sich vom hinterlegten – bitte in Render bei API und Worker genau diesen eintragen.")}</span>}
          </span>
        </p>
      )}
      {unified && (
        <p className="mt-2.5 text-[12.5px] text-fg-muted">
          {t("Ersatz über „Shipment Tracking – Unified“ (wenn Parcel DE den Schlüssel ablehnt oder kein Versandkonto passt): heute")} <span className="tabular-nums font-medium text-fg">{fmtNumber(unified.today)}</span> {t("von")} {fmtNumber(unified.limit)} {t("Abfragen.")}
          {unified.today >= unified.limit && <span className="text-warning"> {t("Kontingent für heute erreicht – weiter ab morgen früh.")}</span>}
        </p>
      )}
      <p className="mt-2 text-[11.5px] text-fg-subtle">{t("Der eingegebene Schlüssel wird nur für den Test an DHL geschickt und nicht gespeichert.")}</p>
    </Card>
  );
}

function AccountCard({ a, manage, checking, onCheck, onEdit, onDelete }: { a: CarrierAccount; manage: boolean; checking: boolean; onCheck: () => void; onEdit: () => void; onDelete: () => void }) {
  const s = STATUS[a.status];
  return (
    <Card className="p-4">
      <div className="flex flex-wrap items-start gap-3">
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-[14px]">{t(a.label)}</p>
          <p className="text-[12.5px] text-fg-muted">{a.carrierName} {t("· Benutzer")} <span className="font-mono">{a.username}</span></p>
        </div>
        <Badge tone={s.tone} icon={s.icon}>{t(s.label)}</Badge>
      </div>
      {a.status === "failed" && a.lastError && <p className="mt-2 flex gap-1.5 text-[12.5px] text-danger"><AlertTriangle className="size-3.5 mt-0.5 shrink-0" />{a.lastError}</p>}
      <div className="mt-3 flex flex-wrap gap-1.5">
        {a.methods.length ? a.methods.map((m) => <Badge key={m}>{m}</Badge>) : <span className="text-[12.5px] text-fg-subtle">{t("Noch keiner Versandart zugeordnet – wird so nicht verwendet.")}</span>}
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-2 text-[12px] text-fg-subtle">
        {a.checkedAt && <span>{t("geprüft")} {fmtRelative(a.checkedAt)}</span>}
        {a.usageToday && <span>{t("· heute")} {fmtNumber(a.usageToday.pieces)} {t("Sendungen abgefragt")}{a.dailyLimit ? t(" von {v}", { v: fmtNumber(a.dailyLimit) }) : ""}</span>}
        {manage && (
          <span className="ml-auto flex gap-1">
            <Button size="sm" variant="ghost" icon={<RefreshCw className={cx("size-3.5", checking && "animate-spin")} />} onClick={onCheck} disabled={checking}>{t("Prüfen")}</Button>
            <Button size="sm" variant="ghost" icon={<Pencil className="size-3.5" />} onClick={onEdit}>{t("Bearbeiten")}</Button>
            <Button size="sm" variant="ghost" icon={<Trash2 className="size-3.5" />} onClick={onDelete} aria-label={t("{label} löschen", { label: a.label })} />
          </span>
        )}
      </div>
    </Card>
  );
}

function GkpHint() {
  return (
    <p className="text-[12.5px] text-fg-muted leading-relaxed">
      <span className="font-medium text-fg">{t("Welcher Zugang?")}</span> {t("Benutzername und Passwort aus dem DHL-Geschäftskundenportal. Am besten einen eigenen System-Benutzer nur für Logiful anlegen – dann bleibt euer persönliches Login privat und ihr könnt den Zugang jederzeit sperren. Das Passwort wird verschlüsselt gespeichert und nie wieder angezeigt.")}
    </p>
  );
}

function AccountDialog({
  account,
  carriers,
  methods,
  scopeNote,
  onClose,
  save,
  onSaved,
}: {
  account: CarrierAccount | null;
  carriers: CarrierInfo[];
  methods: Method[];
  scopeNote?: string;
  onClose: () => void;
  save: (body: Partial<CarrierAccountInput>, id?: string) => Promise<{ account: CarrierAccount; check: CarrierCheck | null }>;
  onSaved: () => void;
}) {
  const [f, setF] = useState({
    carrier: account?.carrier ?? carriers[0]?.key ?? "dhl",
    label: account?.label ?? "",
    username: account?.username ?? "",
    password: "",
    methods: account?.methods ?? [],
    dailyLimit: account?.dailyLimit ? String(account.dailyLimit) : "",
  });
  const m = useMutation({
    mutationFn: () => {
      const limit = f.dailyLimit.trim() ? Number(f.dailyLimit) : null;
      const body = { label: f.label.trim(), username: f.username.trim(), methods: f.methods, dailyLimit: limit, ...(f.password ? { password: f.password } : {}) };
      return account ? save(body, account.id) : save({ ...body, carrier: f.carrier, password: f.password });
    },
    onSuccess: (r) => {
      announce(r.check, account ? "Gespeichert" : "Versandkonto angelegt");
      onSaved();
      onClose();
    },
    onError: (e) => toast.error(errMsg(e, "Speichern fehlgeschlagen")),
  });
  const valid = f.label.trim() && f.username.trim() && (account || f.password) && (!f.dailyLimit || Number(f.dailyLimit) >= 20);
  const toggle = (id: string) => setF((x) => ({ ...x, methods: x.methods.includes(id) ? x.methods.filter((y) => y !== id) : [...x.methods, id] }));
  const carrier = carriers.find((c) => c.key === f.carrier);
  return (
    <Dialog.Root open onOpenChange={(o) => !o && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[60] bg-black/30 animate-fade-in" />
        <Dialog.Content className="fixed z-[70] left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[min(520px,calc(100vw-32px))] max-h-[calc(100vh-32px)] overflow-y-auto rounded-2xl border border-border bg-surface p-6 shadow-2xl animate-pop-in">
          <div className="flex items-start gap-3">
            <div className="flex-1">
              <Dialog.Title className="text-[17px] font-semibold">{account ? t("Versandkonto bearbeiten") : t("Versandkonto hinzufügen")}</Dialog.Title>
              <Dialog.Description className="text-[12.5px] text-fg-muted mt-0.5">{carrier?.hint ?? t("Zugangsdaten beim Versanddienstleister")}</Dialog.Description>
            </div>
            <Dialog.Close className="size-8 -mr-2 -mt-1 grid place-items-center rounded-lg text-fg-subtle hover:bg-surface-2" aria-label={t("Schließen")}><X className="size-4" /></Dialog.Close>
          </div>
          <form className="mt-5 space-y-4" onSubmit={(e) => { e.preventDefault(); if (valid) m.mutate(); }}>
            {!account && carriers.length > 1 && (
              <label className="block text-[12.5px] font-medium">
                {t("Versanddienstleister")}
                <select className="mt-1 w-full h-8 rounded-lg border border-border bg-surface px-2 text-[13px]" value={f.carrier} onChange={(e) => setF({ ...f, carrier: e.target.value })}>
                  {carriers.map((c) => <option key={c.key} value={c.key}>{c.name}</option>)}
                </select>
              </label>
            )}
            <label className="block text-[12.5px] font-medium">
              {t("Name")}
              <Input className="mt-1" value={f.label} onChange={(e) => setF({ ...f, label: e.target.value })} placeholder={t("z. B. DHL-Vertrag Nordlicht")} autoFocus maxLength={80} />
            </label>
            <div className="grid sm:grid-cols-2 gap-3">
              <label className="block text-[12.5px] font-medium">
                {t("Benutzername")}
                <Input className="mt-1" value={f.username} onChange={(e) => setF({ ...f, username: e.target.value })} autoComplete="off" spellCheck={false} maxLength={120} />
              </label>
              <label className="block text-[12.5px] font-medium">
                {t("Passwort")}
                <Input className="mt-1" type="password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} autoComplete="new-password" placeholder={account ? t("unverändert") : ""} maxLength={200} />
              </label>
            </div>
            <fieldset>
              <legend className="text-[12.5px] font-medium">{t("Gilt für diese Versandarten")}</legend>
              {scopeNote && <p className="text-[12px] text-fg-muted">{scopeNote}</p>}
              <div className="mt-1.5 max-h-48 overflow-y-auto rounded-lg border border-border divide-y divide-border">
                {methods.length === 0 && <p className="px-3 py-2.5 text-[12.5px] text-fg-muted">{t("Keine Versandarten bekannt.")}</p>}
                {methods.map((x) => (
                  <label key={x.id} className="flex items-center gap-2.5 px-3 py-2 text-[13px] cursor-pointer hover:bg-surface-2/60">
                    <input type="checkbox" className="size-3.5 accent-ink" checked={f.methods.includes(x.id)} onChange={() => toggle(x.id)} />
                    <span className="font-medium">{x.id}</span>
                    {x.carrier && <span className="text-fg-muted">{x.carrier}</span>}
                  </label>
                ))}
              </div>
            </fieldset>
            <details className="text-[12.5px]">
              <summary className="cursor-pointer text-fg-muted hover:text-fg">{t("Erweitert: Tageskontingent")}</summary>
              <label className="mt-2 block">
                {t("Höchstens so viele Sendungen am Tag abfragen")}
                <Input className="mt-1 w-40" inputMode="numeric" value={f.dailyLimit} onChange={(e) => setF({ ...f, dailyLimit: e.target.value.replace(/\D/g, "").slice(0, 7) })} placeholder="10.000" />
                <span className="block mt-1 text-fg-subtle">{t("Leer = Standard laut DHL (10.000 am Tag). Ist es erreicht, geht es am nächsten Morgen weiter.")}</span>
              </label>
            </details>
            <div className="flex justify-end gap-2 pt-1">
              <Button type="button" variant="ghost" onClick={onClose}>{t("Abbrechen")}</Button>
              <Button variant="primary" loading={m.isPending} disabled={!valid}>{account ? t("Speichern") : t("Hinzufügen und prüfen")}</Button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
